import { IDefinition } from "@pages/PageUtils";
import { _DeepPartialArray, _DeepPartialObject } from "chart.js/dist/types/utils";
import React, { MouseEvent } from "react";

import { IComplexFilter, IValueInterval } from "./components/smart/smartValueHelper";
import { IAppContext } from "./contexts/appContext/AppContext.types";

export type TValue =
    string
    | number
    | boolean
    | Date
    | string[]
    | number[]
    | boolean[]
    | IValueInterval
    | IComplexFilter[]
    | TRecordAny
    | TRecordAny[];
export type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

export type ValueOf<T> = T[keyof T];
/** TS wrapper to make tuple from array. Useful for Promise arrays. */
export const toTuple = <T extends unknown[]>(args: [...T]): T => args;

export type TRecordAny = Record<string, any>;
export type TRecordString = Record<string, string>;
export type TRecordValue = Record<string, TValue>;
export type TRecordType<Type> = Record<string, Type>;
export type EmptyObject = Record<string, never>;
// to be used instead of TS Object, as recommended by linter
/**
 * Don't use `Object` as a type. The `Object` type actually means "any non-nullish value", so it is marginally better than `unknown`.
 * - If you want a type meaning "any object", you probably want `Record<string, unknown>` instead.
 * - If you want a type meaning "any value", you probably want `unknown` instead.
 * */
// todo, remove TRecordAny and replace it with TObject
export type TObject = Record<string, unknown>;

export interface IToString {
    toString: () => string;
}

export type TDefinitionFn = (context: IAppContext) => IDefinition;

// meta -> macOS "cmd" key (but also "winlogo" on windows).
export type IModifierKeys = Partial<Pick<MouseEvent, "shiftKey" | "ctrlKey" | "altKey" | "metaKey">>;

/** reacts setState function signature */
export type TSetState<P, S> = <K extends keyof S>(
    state: ((prevState: Readonly<S>, props: Readonly<P>) => (Pick<S, K> | S | null)) | (Pick<S, K> | S | null),
    callback?: () => void
) => void;

export type RequireKeys<Type, K extends keyof Type> = Omit<Type, K> & Required<Pick<Type, K>>;
// if given interface of array of promises, returns interface for array of awaited values
// made with the help of BingGPT
export type AwaitedArray<T extends unknown[]> = {
    [P in keyof T]: T[P] extends Promise<infer U> ? Awaited<U> : unknown;
};

// DeepPartial implementation taken from the utility-types NPM package, which is
// Copyright (c) 2016 Piotr Witek <piotrek.witek@gmail.com> (http://piotrwitek.github.io)
// and used under the terms of the MIT license
// eslint-disable-next-line @typescript-eslint/ban-types
export type DeepPartial<T> = T extends Function
    ? T
    : T extends Array<infer U>
        ? _DeepPartialArray<U>
        : T extends object
            ? _DeepPartialObject<T>
            : T | undefined;

export interface IEnumEntity {
    Code: string;
    Name: string;
}

export type TClickAction = (args: React.MouseEvent) => void;